import {
  Icon,
  Text,
  Stack,
  Heading,
  Image,
  Flex,
  Avatar,
  Grid,
  GridItem,
  Divider
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { partition, reject } from 'lodash-es';
import { useStatsigClient } from '@statsig/react-bindings/src';

import {
  SFPieChartIcon,
  SFChatBubblesIconOutlined,
  SFCalendarIcon,
  SFRecordCircleIcon,
  SFClipboardIcon,
  SFFocusIcon,
  SFPersonIconOutlined,
  SFEnvelopeIcon,
  SFLineChartIcon,
  ZoomLogoIcon,
  SFThreePersonIconOutlined,
  SFBookPagesOutlinedIcon,
  SFDollarSignCircleIcon,
  SFSliderHorizontalIcon,
  LabelIcon,
  SFBoltFillIcon,
  SFDollarSignIcon,
  SFLensIcon,
  SFClockIcon
} from '@assets/icons';
import { BlackOktaAura } from '@assets/images';
import { useIsUserType, useSetDocumentTitle } from '@util/hooks';
import { useUserStore } from '@store/app.store';

const basePath = '/settings';

const LabelRouteIcon = () => (
  <Icon
    as={LabelIcon}
    boxSize={6}
    color='brandPrimary.primary'
  />
);

function SubRouteCard({ icon, iconType = 'svg', title, headerChildren, description }) {
  return (
    <Stack
      layerStyle='section-card'
      gap={3}
      p={4}
      height='250px'
      data-testid={title}
    >
      <Flex justify='space-between'>
        {iconType === 'avatar' ? (
          <Avatar
            size={15}
            src={icon}
          />
        ) : (
          <Flex
            align='center'
            w={9}
            h={9}
          >
            {iconType === 'png' && (
              <Image
                src={icon}
                alt='Okta Logo'
                boxSize='30px'
              />
            )}
            {iconType === 'svg' && (
              <Icon
                as={icon}
                boxSize={6}
                color='brandPrimary.onContainer'
              />
            )}
          </Flex>
        )}
        {headerChildren}
      </Flex>
      <Heading
        size='sm'
        lineHeight={5}
      >
        {title}
      </Heading>
      <Text
        color='surface.onSurfaceVariant'
        fontSize='sm'
        lineHeight='21px'
        noOfLines={5}
        textOverflow='ellipsis'
      >
        {description}
      </Text>
    </Stack>
  );
}

export default function SettingsPage() {
  useSetDocumentTitle('Settings - FORA');
  const userPhoto = useUserStore(
    (state) => state?.user?.avatar_url || state?.user?.apollo_profile?.photo_url
  );
  const tenant = useUserStore((state) => state?.tenant);
  const isEnterpriseUser = useUserStore((state) => state?.isEnterpriseUser)();
  const isAdmin = useIsUserType('Admin');
  const isExecutive = useIsUserType('Executive');
  const isAssociate = useIsUserType('Associate');

  const { checkGate } = useStatsigClient();
  const enableComponentTesting = checkGate('component-testing-page');
  const enableZoomSettings = checkGate('zoom-settings-page');
  const enableRecordingsAdmin = checkGate('recordings-admin-page');
  const enableEarningsCalls = checkGate('earnings-calls');
  const enableUserProfilePage = checkGate('user-profile-page');
  const subRoutes = [
    {
      hidden: !enableUserProfilePage,
      path: '/account',
      iconType: 'avatar',
      icon: userPhoto,
      title: 'Your Profile Settings',
      description: 'Review and add settings to get the most out of FORA.',
      colSpan: 2
    },
    {
      hidden: isAssociate || enableUserProfilePage,
      path: '/prompts',
      icon: SFClipboardIcon,
      title: 'User Prompt',
      description:
        "Train FORA to understand you by providing a description of your role and the kinds of summaries & insights you're looking for."
    },
    {
      hidden: isAssociate,
      path: '/sources',
      icon: SFPieChartIcon,
      title: 'Data Sources',
      description: 'Manage external sources of information and reports.'
    },
    {
      hidden: !isAdmin,
      path: '/manage-labels',
      icon: LabelRouteIcon,
      title: 'Manage Labels',
      description: 'Add & manage labels for your company.',
      isAdmin: true
    },
    {
      hidden: !isEnterpriseUser,
      path: '/focuses',
      isGlobalPath: true,
      icon: SFFocusIcon,
      title: 'Categories',
      description: 'Help FORA categorize meetings.',
      isAdmin: true
    },
    {
      hidden: !(isExecutive || isAdmin),
      path: '/manage-users',
      icon: SFThreePersonIconOutlined,
      title: 'Manage Users',
      description: 'Add & remove users.',
      isAdmin: true
    },
    {
      hidden: !(isExecutive || isAdmin),
      path: '/analytics',
      icon: SFLineChartIcon,
      title: 'Analytics',
      description: 'View data on FORA usage',
      isAdmin: true
    },
    {
      hidden: !(isExecutive || isAdmin),
      path: '/hours-nylas-users',
      icon: SFClockIcon,
      title: 'Hours & Nylas Users',
      description:
        'View the number of bot hours used per month and see which users have an associated Nylas account for automated bot scheduling.',
      isAdmin: true
    },
    {
      hidden: isEnterpriseUser || !(isExecutive || isAdmin),
      path: '/billing',
      icon: SFDollarSignIcon,
      title: 'Subscription & Billing',
      description: 'Manage your subscription plan and billing information',
      isAdmin: true
    },
    {
      hidden: true,
      path: '/security',
      icon: SFPersonIconOutlined,
      title: 'Sign-In & Security',
      description: 'Secure your account and update your password'
    },
    {
      hidden: isAssociate,
      path: '/calendar',
      icon: SFCalendarIcon,
      title: 'Calendar',
      description: 'Add & remove calendars and manage calendar settings.'
    },
    {
      hidden:
        !(tenant?.slack_config?.access_token || tenant?.teams_config?.access_token) || isAssociate,
      path: '/manage-digests',
      icon: SFChatBubblesIconOutlined,
      title: 'Slack & Teams Digests',
      description:
        "Manage how FORA summarizes information from your organizations's Slack & Teams messages"
    },
    {
      hidden: isAssociate || enableUserProfilePage,
      path: '/email-subscriptions',
      icon: SFEnvelopeIcon,
      title: 'Email Subscriptions',
      description:
        'Specify which email subscriptions you want to receive and when you want to receive them.'
    },
    {
      hidden: !enableEarningsCalls || !(isAdmin || isExecutive) || !isEnterpriseUser,
      path: '/earnings-calls',
      icon: SFDollarSignCircleIcon,
      title: 'Earnings Calls',
      description: 'Add stock tickers to subscribe to earnings call summaries.'
    },
    {
      hidden: !isAdmin,
      path: '/recording',
      icon: SFRecordCircleIcon,
      title: 'Recording',
      description:
        'Manage recording settings including important keywords and meeting titles & domainsto exclude from recording.',
      isAdmin: true
    },
    {
      hidden: !enableZoomSettings,
      path: '/zoom',
      icon: ZoomLogoIcon,
      title: 'Zoom Admin',
      description: 'Manage zoom account settings to improve the FORA recording experience',
      isAdmin: true
    },
    {
      hidden: !enableRecordingsAdmin,
      path: '/recordings-admin',
      icon: SFRecordCircleIcon,
      title: 'Recordings Admin',
      description: 'Administrative controls for managing recordings and their processing status',
      isAdmin: true
    },
    {
      path: '/manage-templates?templatesSort=1|desc',
      icon: SFBookPagesOutlinedIcon,
      title: 'Manage Templates',
      description: 'Manage your summary templates'
    },
    {
      path: '/manage-uploads?templatesSort=2|desc',
      icon: SFBoltFillIcon,
      title: 'Manage Uploads',
      description: 'Manage your uploaded meetings and documents'
    },
    {
      path: '/lenses',
      icon: SFLensIcon,
      title: 'Lenses',
      description: 'View saved Lenses used to generate insights'
    },
    {
      hidden: true, // TODO: Eventually we will allow users to configure this
      path: '/configure-okta',
      icon: BlackOktaAura,
      iconType: 'png',
      title: 'Configure Okta',
      description: 'Configure your tenant for login via Okta',
      isAdmin: true
    },
    {
      hidden: !enableComponentTesting,
      path: '/component-testing',
      isGlobalPath: true,
      icon: SFSliderHorizontalIcon,
      title: 'Component Testing',
      description: 'Preview components'
    }
  ];

  const filteredSubroutes = reject(subRoutes, 'hidden');
  const [adminSubRoutes, userSubroutes] = partition(filteredSubroutes, 'isAdmin');

  return (
    <Stack
      p={11}
      gap={{ base: 6, xl: 11 }}
    >
      <Heading size='lg'>Settings</Heading>
      <SettingsGrid subRoutes={userSubroutes} />

      {(isAdmin || isExecutive) && (
        <>
          <Divider />
          <Heading size='lg'>Admin Settings</Heading>
          <SettingsGrid subRoutes={adminSubRoutes} />
        </>
      )}
    </Stack>
  );
}

function SettingsGrid({ subRoutes }) {
  return (
    <Grid
      templateColumns={{
        base: 'repeat(1, 1fr)',
        md: 'repeat(2, 1fr)',
        lg: 'repeat(3, 1fr)',
        xl: 'repeat(4, 1fr)'
      }}
      gap={{ base: 4, md: 6, lg: 8, xl: 11 }}
    >
      {subRoutes.map((subRoute) => {
        const path = subRoute.isGlobalPath ? subRoute.path : basePath + subRoute.path;
        return (
          <GridItem
            as={Link}
            to={!subRoute.disabled && path}
            key={path}
            state={{ title: subRoute.title, description: subRoute.description }}
            colSpan={{ base: 1, md: subRoute.colSpan ?? 1 }}
          >
            <SubRouteCard
              icon={subRoute.icon}
              iconType={subRoute.iconType}
              title={subRoute.title}
              description={subRoute.description}
              headerChildren={subRoute.headerChildren}
              props={subRoute.props}
            />
          </GridItem>
        );
      })}
    </Grid>
  );
}
